/**
 * 계약 상태 전이. 저장소는 여기서 정한 것만 쓰고 스스로 판단하지 않는다.
 *
 *   DRAFT ──발송──▶ SENT ──고객 동의·담당자 기록──▶ SIGNED ──▶ TERMINATED
 *     │  └──────── 종이 계약(담당자 날인 기록) ───────┘        └──▶ COMPLETED
 *     └──▶ VOID ◀── SENT (고객 거절 · 재작성)
 *
 * - **`SIGNED` 는 `VOID` 로 못 간다.** 체결된 계약을 지우는 전이는 실무에 없다 —
 *   변경은 변경공사, 파기는 `TERMINATED` 다(노트 `12` 열린 질문 C)
 * - **`DRAFT → SIGNED` 는 있다.** 종이에 도장 찍는 현장은 링크를 안 보낸다
 * - **`COMPLETED` 는 `project` 준공이 찍는다.** 계약이 현장을 끝내지 않는다
 * - `VOID`·`TERMINATED` 는 **살아 있는 고객 링크를 회수**한다(`revokesShare`)
 *
 * 여기 patch 들은 `0019_contract.sql` 의 check 와 한 몸이다. 저장소가 제 손으로
 * 열을 조립하면 「체결됐다는데 `signed_at` 이 빈」 행이 생기고, 그 행은 DB 가 아니라
 * **화면에서 조용히 이상하게** 보인다.
 */

import { CONTRACT_STATUS_LABEL, ContractSigner, ContractStatus } from "./types";

export const CONTRACT_TRANSITIONS: Record<
  ContractStatus,
  readonly ContractStatus[]
> = {
  DRAFT: ["SENT", "SIGNED", "VOID"],
  SENT: ["SIGNED", "VOID"],
  SIGNED: ["TERMINATED", "COMPLETED"],
  VOID: [],
  TERMINATED: [],
  COMPLETED: [],
};

export const canTransition = (
  from: ContractStatus,
  to: ContractStatus
): boolean => CONTRACT_TRANSITIONS[from].includes(to);

// 사용자에게 나가는 문구라 상태는 한글 라벨로 적는다.
export const contractTransitionMessage = (from: ContractStatus, to: ContractStatus): string =>
  `계약을 「${CONTRACT_STATUS_LABEL[from]}」에서 「${CONTRACT_STATUS_LABEL[to]}」 상태로 바꿀 수 없습니다`;

export const assertTransition = (
  from: ContractStatus,
  to: ContractStatus
): void => {
  if (!canTransition(from, to)) {
    throw new Error(contractTransitionMessage(from, to));
  }
};

export const canSend = (status: ContractStatus): boolean =>
  canTransition(status, "SENT");

export const canSign = (status: ContractStatus): boolean =>
  canTransition(status, "SIGNED");

export const canVoid = (status: ContractStatus): boolean =>
  canTransition(status, "VOID");

export const canTerminate = (status: ContractStatus): boolean =>
  canTransition(status, "TERMINATED");

export const canComplete = (status: ContractStatus): boolean =>
  canTransition(status, "COMPLETED");

/** 초안 편집. 발송 후에는 VOID 하고 새로 만든다(`replaces_contract_id`). */
export const canEditDraft = (status: ContractStatus): boolean =>
  status === "DRAFT";

/**
 * 이 상태로 가면 살아 있는 고객 링크를 회수해야 하는가.
 *
 * 체결(`SIGNED`)은 회수하지 **않는다** — 고객이 체결한 계약서를 계속 봐야 한다
 * (그래서 견적의 30일을 그대로 쓰지 않고 만료를 연장한다 · 노트 `12`).
 */
export const revokesShare = (to: ContractStatus): boolean =>
  to === "VOID" || to === "TERMINATED";

/** 계약이 더는 움직이지 않는 상태. 목록의 「끝난 건」 필터가 본다. */
export const isClosed = (status: ContractStatus): boolean =>
  CONTRACT_TRANSITIONS[status].length === 0;

/* ── 열 patch ─────────────────────────────────────────────────── */

export interface ContractSendPatch {
  status: Extract<ContractStatus, "SENT">;
  sent_at: string;
}

export const applySend = (
  from: ContractStatus,
  at: string
): ContractSendPatch => {
  assertTransition(from, "SENT");
  return { status: "SENT", sent_at: at };
};

export interface ContractSignInput {
  by: ContractSigner;
  /** 서명한 사람의 이름. **빈 문자열은 증거가 아니다**(`contract_signed_evidence`). */
  name: string;
  /** `contract_snapshot.canonical` 의 sha256. 스냅샷 없이 체결하지 않는다. */
  doc_hash: string;
  /** 고객이 직접 누른 경우에만. 담당자 기록에 넣으면 담당자 흔적이 고객 것처럼 보인다. */
  ip?: string | null;
  user_agent?: string | null;
  note?: string;
}

export interface ContractSignPatch {
  status: Extract<ContractStatus, "SIGNED">;
  signed_at: string;
  signed_by: ContractSigner;
  signed_name: string;
  signed_doc_hash: string;
  signed_ip: string | null;
  signed_user_agent: string | null;
  signed_note: string;
}

/**
 * 체결. **증거 네 열은 같이 있거나 같이 없다** — 여기서 한 번에 만들어야
 * 저장소가 세 개만 채우는 일이 없다.
 *
 * **고객은 링크로만 서명한다.** 링크를 보낸 적 없는 계약에 고객 서명이 찍혀 있으면
 * 그건 담당자가 대신 누른 것이고, 담당자 서명은 `STAFF` 다
 * (`contract_customer_signed_via_link`). DB 에서 23514 로 튕기기 전에 막는다.
 *
 * **`sent_at` 이 아니라 `from` 으로 판정한다.** 시각을 파라미터로 받으면 `from` 과
 * 어긋난 값을 넘길 수 있고(`DRAFT` + 시각), 그 patch 는 `contract_sent_status` 가
 * `sent_at is null` 을 보장하는 `DRAFT` 행에 그대로 들어가 23514 로 죽는다.
 * `from === "SENT"` 가 곧 `sent_at is not null` 이라 **인자가 없으면 어긋날 수 없다**.
 */
export const applySign = (
  from: ContractStatus,
  input: ContractSignInput,
  at: string
): ContractSignPatch => {
  assertTransition(from, "SIGNED");

  const name = input.name.trim();
  const hash = input.doc_hash.trim();
  if (name === "") {
    throw new Error("서명자 이름이 비어 있습니다");
  }
  if (hash === "") {
    throw new Error("체결 문서 해시가 비어 있습니다 — 스냅샷을 먼저 만들어야 합니다");
  }
  if (input.by === "CUSTOMER" && from !== "SENT") {
    throw new Error("고객 링크를 보낸 적이 없는 계약입니다 — 담당자 기록으로 남겨 주세요");
  }

  const isCustomer = input.by === "CUSTOMER";
  return {
    status: "SIGNED",
    signed_at: at,
    signed_by: input.by,
    signed_name: name,
    signed_doc_hash: hash,
    // 담당자 기록에는 흔적을 남기지 않는다(`contract_staff_sign_has_no_trace`).
    signed_ip: isCustomer ? (input.ip ?? null) : null,
    signed_user_agent: isCustomer ? (input.user_agent ?? null) : null,
    signed_note: input.note ?? "",
  };
};

export interface ContractVoidPatch {
  status: Extract<ContractStatus, "VOID">;
  voided_at: string;
  void_reason: string;
}

export const applyVoid = (
  from: ContractStatus,
  at: string,
  reason?: string | null
): ContractVoidPatch => {
  assertTransition(from, "VOID");
  return { status: "VOID", voided_at: at, void_reason: (reason ?? "").trim() };
};

export interface ContractTerminatePatch {
  status: Extract<ContractStatus, "TERMINATED">;
  terminated_at: string;
  terminate_reason: string;
  /** 기성고 정산액. **해지한 계약에만** 있다(`contract_settled_status`). */
  settled_amount: number | null;
}

/**
 * 해지. **사유가 필수다**(2026-09-22 오너 확정) — 실제 사고 대부분이 중도 해지
 * 정산인데, 사유 없는 해지 기록은 몇 달 뒤 아무도 이유를 모른다. 나중에 채우는
 * 경로가 따로 없으므로 여기서 받아야 남는다.
 *
 * `VOID` 는 다르다 — 초안을 접는 일은 사유가 없을 수 있다.
 */
export const applyTerminate = (
  from: ContractStatus,
  at: string,
  reason: string,
  settledAmount?: number | null
): ContractTerminatePatch => {
  assertTransition(from, "TERMINATED");
  if (reason.trim() === "") {
    throw new Error("해지 사유를 적어 주세요");
  }
  const settled = settledAmount ?? null;
  if (settled !== null && (!Number.isSafeInteger(settled) || settled < 0)) {
    throw new Error("기성고 정산액은 0 이상의 정수여야 합니다");
  }
  return {
    status: "TERMINATED",
    terminated_at: at,
    terminate_reason: reason.trim(),
    settled_amount: settled,
  };
};

export interface ContractCompletePatch {
  status: Extract<ContractStatus, "COMPLETED">;
  completed_at: string;
}

/** 준공. **`project` 가 부른다** — 계약 화면에 이 버튼을 두지 않는다(노트 `12` 5절). */
export const applyComplete = (
  from: ContractStatus,
  at: string
): ContractCompletePatch => {
  assertTransition(from, "COMPLETED");
  return { status: "COMPLETED", completed_at: at };
};

/**
 * 미체결 행의 증거 네 열. **`''` 가 아니라 `null` 이다** — 이 표의 다른 text 열이
 * `default ''` 라 저장소가 같은 습관으로 빈 문자열을 보내면 초안 저장이 통째로
 * 막힌다(`contract_signed_evidence`). 저장소가 초안을 만들 때 이걸 펼친다.
 */
export const UNSIGNED_EVIDENCE = {
  signed_at: null,
  signed_by: null,
  signed_name: null,
  signed_doc_hash: null,
  signed_ip: null,
  signed_user_agent: null,
} as const;
